<template>
  <section>
    <VProgress class="offset-top-2 offset-bottom-2" :steps="steps" active="cita.yourconfirmation" />
    <span data-test="confirmtext">
      <span v-html="confirmText" />
      <span v-if="!isPageTypeNonAnonymousCapOtp"
        >:
        <b><VPrice :footnotes="footnotes" /></b>
      </span>
    </span>

    <VForm :form="form" @submit="onNext">
      <VInput name="otpInput" :label="$t('cita.youridentcode')" :hint="$t('errorcodes.WRONG_OTP')" :placeholder="$t('cita.inputcode')" required />

      <div v-if="isPageTypeNonAnonymousCapOtp" class="offset-top-2 text-right">
        <VButton
          type="button"
          class="offset-left-1 offset-bottom-1 btn-default"
          label="cita.label_request_new_code"
          :disabled="isBusy || isOtpResendDisabled"
          @click="onResendOtp"
        />
      </div>

      <transition name="fade" mode="out-in" appear>
        <div v-if="isOtpResendDisabled" class="notification notification-negative" role="alert">
          <div class="notification-left">
            <div class="icon-frame">
              <VSvgIcon name="information" variant="outlined" size="large" />
            </div>
          </div>
          <div class="notification-content">
            <div class="notification-heading offset-top-1">
              {{ $t('errorcodes.OTP_RESEND_LIMIT_EXCEEDED') }}
            </div>
          </div>
        </div>
      </transition>

      <div v-if="isPageTypeNonAnonymousCapOtp" data-test="captchatext">
        {{ $t('cita.confirm_text_captcha2') }}
      </div>

      <img v-if="captcha" :src="'data:image/jpeg;base64,' + captcha" alt="captcha" />

      <VInput
        name="captchaInput"
        :label="$t('cita.yourcaptcha')"
        :hint="$t('errorcodes.CAPTCHA_INVALID')"
        :placeholder="$t('cita.input_captcha')"
        :disabled="!isCaptchaReloadAllowed"
        required
      />
      <div class="offset-top-2 text-right">
        <VButton
          v-if="captcha"
          type="button"
          class="offset-left-1 offset-bottom-1 btn-default"
          label="cita.label_request_new_captcha"
          :disabled="isBusy || !isCaptchaReloadAllowed"
          @click="onReloadCaptcha"
        />

        <div v-if="isPageTypeNonAnonymousCapOtp" class="text-left" data-test="confirmtext2">
          {{ $t('cita.confirm_text_captcha3') }}:
          <b><VPrice :footnotes="footnotes" /></b>
        </div>

        <hr />

        <transition name="fade" mode="out-in" appear>
          <VSpinner v-if="isBusy" slim class="pull-left" />
        </transition>
        <VButton v-focus type="submit" class="offset-left-1 offset-bottom-1 btn btn-default btn-brand" label="cita.label_confirm" :disabled="isBusy" />
        <VButton tag="a" class="offset-left-1 offset-bottom-1 btn btn-default" label="cita.abort" :disabled="isBusy" @click.prevent="onAbort" />
      </div>
    </VForm>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import TitleMixin from '@/mixins/title';
import CitaStepsMixin from '@/mixins/citaSteps';
import OtpResendMixin from '@/mixins/otpResend';
import VSpinner from '@/components/Spinner';
import VPrice from '@/components/Cita/Price';
import VForm from '@/components/Form/Form';
import Proxy from '@/proxies/CitaProxy';
import { safeNavigate } from '@/utils/safeUrl';
import focus from '@/directives/focus';
import { focusFirstError, initFields } from '@/utils/form';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';
export default {
  name: 'CitaConfirm',

  directives: {
    focus,
  },

  components: {
    VSvgIcon,
    VSpinner,
    VPrice,
    VForm,
    VInput: () => import('@/components/Form/Input'),
    VProgress: () => import('@/components/Progress'),
    VButton: () => import('@/components/Button'),
  },

  mixins: [TitleMixin, CitaStepsMixin, OtpResendMixin],

  props: {
    footnotes: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      proxy: new Proxy(),
      form: initFields(
        { name: 'otpInput', isHidden: !this.$store.getters['cita/isPageTypeNonAnonymousXtcOtp'] && !this.$store.getters['cita/isPageTypeNonAnonymousCapOtp'] },
        { name: 'captchaInput', isHidden: !this.$store.state.cita.captcha }
      ),
      isBusy: false,
      isCaptchaReloadAllowed: true,
    };
  },

  computed: {
    ...mapState('system', ['sessionId']),
    ...mapState('cita', ['msisdn', 'refUrl', 'captcha', 'pageType']),
    ...mapGetters('cita', ['isPageTypeNonAnonymousCapOtp', 'isPageTypeNonAnonymousXtcOtp', 'isPageTypeNonAnonymousCaptcha', 'getPageTypeWebtrekk']),

    confirmText() {
      return this.isPageTypeNonAnonymousCaptcha
        ? this.$t('cita.confirm_text_captcha')
        : this.isPageTypeNonAnonymousCapOtp
        ? this.$t('cita.confirm_text_otp1')
        : this.isPageTypeNonAnonymousXtcOtp
        ? this.$tc('cita.infotext4', this.msisdn)
        : this.$tc('cita.confirm_text_simple', this.msisdn);
    },
  },

  created() {
    this.$webtrekk.viewPage(
      `cita.${this.getPageTypeWebtrekk}.bestaetigung${this.pageType === 'ANONYMOUS' ? (this.captcha ? '-mit' : '-ohne') + '-captcha' : ''}`
    );
  },

  methods: {
    async onReloadCaptcha() {
      const response = await this.proxy.captchaReload();
      this.$store.dispatch('cita/setCaptcha', response.captcha);
      this.isCaptchaReloadAllowed = response.isCaptchaReloadAllowed;
      this.$webtrekk.link('cita.label_request_new_captcha');
    },

    async onAbort() {
      this.isBusy = true;
      await this.proxy.confirm(false, this.sessionId, null, null);
      await this.$webtrekk.link('cita.abort', this.refUrl);
      // SECURITY: safeNavigate refuses non-http(s) URLs (blocks javascript:/data: XSS).
      safeNavigate(this.refUrl);
    },

    async onNext({ values, form }) {
      try {
        this.isBusy = true;
        await this.proxy.confirm(true, this.sessionId, values.otpInput, values.captchaInput);
        await this.$webtrekk.link('cita.label_confirm', this.refUrl);
        // SECURITY: safeNavigate refuses non-http(s) URLs (blocks javascript:/data: XSS).
        safeNavigate(this.refUrl);
      } catch (error) {
        switch (error?.data?.code) {
          case 'INVALID_CAPTCHA':
            form.captchaInput.setInvalid();
            break;
          case 'WRONG_OTP':
            form.otpInput.setInvalid();
            break;
          default:
            throw error;
        }
        focusFirstError(form);
        this.isBusy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@scss';
</style>
